import { format, addDays, isWeekend, isSameDay } from 'date-fns';

/**
 * Local Events Service
 * 
 * Provides community activity recommendations and local event integration
 * for enhanced family coordination and activity planning.
 */
class LocalEventsService {
  constructor() {
    this.eventCache = new Map();
    this.locationPreferences = null;
    this.familyInterests = new Set();
    this.ageGroups = new Set();
  }

  /**
   * Initialize service with family context
   */
  initialize(familyMembers = []) {
    // Extract family interests and age groups
    this.familyInterests.clear();
    this.ageGroups.clear();
    
    familyMembers.forEach(member => {
      // Add age group categorization
      if (member.age) {
        if (member.age < 13) this.ageGroups.add('children');
        else if (member.age < 18) this.ageGroups.add('teens');
        else this.ageGroups.add('adults');
      }
      
      // Add interests if available
      if (member.interests) {
        member.interests.forEach(interest => this.familyInterests.add(interest));
      }
    });

    // Set location from weather service or default
    this.locationPreferences = this.getLocationContext();
  }

  /**
   * Get local events and community activities
   */
  async getLocalEvents(options = {}) {
    const {
      dateRange = 7, // days ahead
      category = null,
      ageGroup = null,
      weather = null
    } = options;

    try {
      // For now, generate mock local events
      // In production, this would integrate with:
      // - Local government event APIs
      // - Library system APIs
      // - Community center calendars
      // - School district calendars
      // - Local business events
      
      const events = await this.generateLocalEvents(dateRange, { category, ageGroup, weather });
      return this.filterEventsByRelevance(events);
    } catch (error) {
      console.error('Error fetching local events:', error);
      return this.getFallbackEvents();
    }
  }

  /**
   * Generate contextual activity recommendations
   */
  async getActivityRecommendations(context = {}) {
    const { 
      weather, 
      timeOfDay, 
      familyAvailability, 
      energyLevel = 'normal',
      duration = 120 // minutes
    } = context;

    const recommendations = [];

    // Weather-based recommendations
    if (weather) {
      const weatherActivities = this.getWeatherBasedActivities(weather, duration);
      recommendations.push(...weatherActivities);
    }

    // Time-based recommendations
    const timeActivities = this.getTimeBasedActivities(timeOfDay, duration);
    recommendations.push(...timeActivities);

    // Family interest-based recommendations
    const interestActivities = this.getInterestBasedActivities(duration);
    recommendations.push(...interestActivities);

    // Energy level appropriate activities
    const energyActivities = this.getEnergyBasedActivities(energyLevel, duration);
    recommendations.push(...energyActivities);

    // Remove duplicates and rank by relevance
    return this.rankRecommendations(recommendations, context);
  }

  /**
   * Get seasonal activity suggestions
   */
  getSeasonalActivities() {
    const now = new Date();
    const month = now.getMonth();
    const season = this.getCurrentSeason(month);
    
    const seasonalActivities = {
      spring: [
        {
          title: 'Cherry Blossom Festival',
          category: 'outdoor',
          description: 'Local park spring festival with food vendors and live music',
          duration: 180,
          cost: 'free',
          ageGroups: ['all'],
          location: 'Central Park'
        },
        {
          title: 'Community Garden Workday',
          category: 'volunteer',
          description: 'Help plant vegetables and flowers at the community garden',
          duration: 120,
          cost: 'free',
          ageGroups: ['children', 'teens', 'adults'],
          location: 'Community Garden Center'
        }
      ],
      summer: [
        {
          title: 'Outdoor Movie Night',
          category: 'entertainment',
          description: 'Free outdoor movie screening with popcorn and drinks',
          duration: 150,
          cost: 'free',
          ageGroups: ['all'],
          location: 'Town Square'
        },
        {
          title: 'Farmers Market',
          category: 'shopping',
          description: 'Weekly farmers market with local produce and crafts',
          duration: 90,
          cost: 'varies',
          ageGroups: ['all'],
          location: 'Main Street'
        }
      ],
      fall: [
        {
          title: 'Fall Festival & Pumpkin Patch',
          category: 'seasonal',
          description: 'Family-friendly fall activities with hayrides and corn maze',
          duration: 240,
          cost: '$15/person',
          ageGroups: ['all'],
          location: 'Harvest Farm'
        },
        {
          title: 'Apple Picking',
          category: 'outdoor',
          description: 'Pick your own apples at local orchards',
          duration: 180,
          cost: '$8/person + apples',
          ageGroups: ['all'],
          location: 'Sunset Orchards'
        }
      ],
      winter: [
        {
          title: 'Holiday Light Tour',
          category: 'seasonal',
          description: 'Self-guided tour of neighborhood holiday light displays',
          duration: 120,
          cost: 'free',
          ageGroups: ['all'],
          location: 'Historic District'
        },
        {
          title: 'Ice Skating',
          category: 'recreation',
          description: 'Public ice skating with rental skates available',
          duration: 90,
          cost: '$12/person + $5 rentals',
          ageGroups: ['children', 'teens', 'adults'],
          location: 'City Ice Rink'
        }
      ]
    };

    return seasonalActivities[season] || [];
  }

  /**
   * Generate mock local events (would be replaced with real API calls)
   */
  async generateLocalEvents(daysAhead, filters = {}) {
    const events = [];
    const baseDate = new Date();

    for (let i = 0; i < daysAhead; i++) {
      const eventDate = addDays(baseDate, i);
      const dayEvents = this.generateDayEvents(eventDate);
      events.push(...dayEvents);
    }

    return events;
  }

  /**
   * Generate events for a specific day
   */
  generateDayEvents(date) {
    const events = [];
    const isWeekendDay = isWeekend(date);
    const dateStr = format(date, 'yyyy-MM-dd');

    // Library events (more frequent on weekends)
    if (Math.random() > (isWeekendDay ? 0.3 : 0.7)) {
      events.push({
        id: `library-${dateStr}`,
        title: isWeekendDay ? 'Family Story Time' : 'Teen Book Club',
        category: 'education',
        date: dateStr,
        time: isWeekendDay ? '10:00' : '16:00',
        duration: 60,
        location: 'Public Library',
        description: isWeekendDay ? 
          'Interactive story time with crafts for ages 3-8' : 
          'Weekly book discussion for teens',
        cost: 'free',
        ageGroups: isWeekendDay ? ['children'] : ['teens'],
        source: 'library'
      });
    }

    // Community center events
    if (Math.random() > 0.6) {
      const activities = [
        'Pottery Class', 'Yoga Session', 'Senior Game Day', 
        'Teen Dance Class', 'Art Workshop'
      ];
      const activity = activities[Math.floor(Math.random() * activities.length)];
      
      events.push({
        id: `community-${dateStr}-${activity.replace(/\s+/g, '-').toLowerCase()}`,
        title: activity,
        category: 'recreation',
        date: dateStr,
        time: '18:00',
        duration: 90,
        location: 'Community Center',
        description: `${activity} for all skill levels`,
        cost: '$5/person',
        ageGroups: activity.includes('Teen') ? ['teens'] : ['adults'],
        source: 'community'
      });
    }

    // Weekend special events
    if (isWeekendDay && Math.random() > 0.5) {
      const specialEvents = [
        {
          title: 'Farmers Market',
          time: '08:00',
          duration: 240,
          location: 'Town Square',
          description: 'Fresh produce, crafts, and local goods',
          cost: 'varies'
        },
        {
          title: 'Park Cleanup Day',
          time: '09:00',
          duration: 180,
          location: 'Riverside Park',
          description: 'Community volunteer event with refreshments',
          cost: 'free'
        }
      ];

      const event = specialEvents[Math.floor(Math.random() * specialEvents.length)];
      events.push({
        id: `special-${dateStr}`,
        category: 'community',
        date: dateStr,
        ageGroups: ['all'],
        source: 'community',
        ...event
      });
    }

    return events;
  }

  /**
   * Get weather-appropriate activities
   */
  getWeatherBasedActivities(weather, duration) {
    const activities = [];
    
    if (weather.suitability?.rating === 'excellent' || weather.suitability?.rating === 'good') {
      // Outdoor activities for good weather
      activities.push(
        {
          title: 'Visit Local Park',
          category: 'outdoor',
          description: 'Great weather for outdoor family time',
          duration: duration,
          confidence: 0.9,
          weatherDependent: true
        },
        {
          title: 'Outdoor Farmers Market',
          category: 'shopping',
          description: 'Perfect weather for browsing local vendors',
          duration: Math.min(duration, 120),
          confidence: 0.8,
          weatherDependent: true
        }
      );
    } else {
      // Indoor activities for poor weather
      activities.push(
        {
          title: 'Visit Local Museum',
          category: 'education',
          description: 'Indoor family activity perfect for today\'s weather',
          duration: duration,
          confidence: 0.8,
          weatherDependent: true
        },
        {
          title: 'Library Programs',
          category: 'education',
          description: 'Check out story time or teen programs',
          duration: Math.min(duration, 90),
          confidence: 0.9,
          weatherDependent: true
        }
      );
    }

    return activities;
  }

  /**
   * Get time-appropriate activities
   */
  getTimeBasedActivities(timeOfDay, duration) {
    const activities = [];
    
    switch (timeOfDay) {
      case 'morning':
        activities.push(
          {
            title: 'Community Breakfast',
            category: 'food',
            description: 'Local diner or community center breakfast',
            duration: 90,
            confidence: 0.7
          },
          {
            title: 'Morning Farmers Market',
            category: 'shopping',
            description: 'Best selection early in the day',
            duration: 120,
            confidence: 0.8
          }
        );
        break;
        
      case 'afternoon':
        activities.push(
          {
            title: 'Matinee Movie',
            category: 'entertainment',
            description: 'Discounted afternoon movie showings',
            duration: 150,
            confidence: 0.6
          },
          {
            title: 'Community Center Classes',
            category: 'recreation',
            description: 'Afternoon programs and activities',
            duration: 90,
            confidence: 0.7
          }
        );
        break;
        
      case 'evening':
        activities.push(
          {
            title: 'Evening Community Events',
            category: 'community',
            description: 'Town meetings, presentations, or social events',
            duration: 120,
            confidence: 0.6
          },
          {
            title: 'Restaurant Week',
            category: 'food',
            description: 'Special dining experiences at local restaurants',
            duration: 120,
            confidence: 0.5
          }
        );
        break;
    }

    return activities.filter(activity => activity.duration <= duration);
  }

  /**
   * Get activities matching family interests
   */
  getInterestBasedActivities(duration) {
    const activities = [];
    
    // Generate activities based on stored family interests
    if (this.familyInterests.has('art')) {
      activities.push({
        title: 'Art Gallery Opening',
        category: 'culture',
        description: 'Local artist showcase with refreshments',
        duration: 120,
        confidence: 0.8
      });
    }

    if (this.familyInterests.has('music')) {
      activities.push({
        title: 'Community Concert',
        category: 'entertainment',
        description: 'Local band or choir performance',
        duration: 90,
        confidence: 0.7
      });
    }

    if (this.familyInterests.has('sports')) {
      activities.push({
        title: 'Local Sports Game',
        category: 'sports',
        description: 'High school or community league game',
        duration: 180,
        confidence: 0.6
      });
    }

    // Add general interest activities
    activities.push(
      {
        title: 'Community Workshop',
        category: 'education',
        description: 'Learn a new skill with local experts',
        duration: 120,
        confidence: 0.5
      },
      {
        title: 'Volunteer Opportunity',
        category: 'volunteer',
        description: 'Give back to the community together',
        duration: 180,
        confidence: 0.6
      }
    );

    return activities.filter(activity => activity.duration <= duration);
  }

  /**
   * Get energy-level appropriate activities
   */
  getEnergyBasedActivities(energyLevel, duration) {
    const activities = [];
    
    switch (energyLevel) {
      case 'energetic':
        activities.push(
          {
            title: 'Community Sports Day',
            category: 'sports',
            description: 'Active outdoor games and competitions',
            duration: 180,
            confidence: 0.8
          },
          {
            title: 'Hiking Group',
            category: 'outdoor',
            description: 'Organized nature walks with local group',
            duration: 240,
            confidence: 0.7
          }
        );
        break;
        
      case 'chill':
        activities.push(
          {
            title: 'Community Garden Tour',
            category: 'leisure',
            description: 'Peaceful walk through local gardens',
            duration: 90,
            confidence: 0.8
          },
          {
            title: 'Book Reading at Library',
            category: 'education',
            description: 'Quiet time with new books and magazines',
            duration: 60,
            confidence: 0.9
          }
        );
        break;
        
      default: // normal
        activities.push(
          {
            title: 'Community Fair',
            category: 'community',
            description: 'Local fair with games, food, and activities',
            duration: 180,
            confidence: 0.6
          },
          {
            title: 'Shopping District Walk',
            category: 'leisure',
            description: 'Browse local shops and cafes',
            duration: 120,
            confidence: 0.5
          }
        );
    }

    return activities.filter(activity => activity.duration <= duration);
  }

  /**
   * Rank recommendations by relevance
   */
  rankRecommendations(recommendations, context) {
    return recommendations
      .map(rec => ({
        ...rec,
        relevanceScore: this.calculateRelevanceScore(rec, context)
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 5); // Top 5 recommendations
  }

  /**
   * Calculate relevance score for recommendations
   */
  calculateRelevanceScore(recommendation, context) {
    let score = recommendation.confidence || 0.5;
    
    // Weather alignment bonus
    if (recommendation.weatherDependent && context.weather) {
      const suitability = context.weather.suitability?.score || 0.5;
      score += suitability * 0.2;
    }

    // Duration match bonus
    if (context.duration) {
      const durationMatch = 1 - Math.abs(recommendation.duration - context.duration) / context.duration;
      score += Math.max(0, durationMatch) * 0.1;
    }

    // Family interest bonus
    if (this.familyInterests.size > 0) {
      // Simple category matching (would be more sophisticated in production)
      if (this.familyInterests.has(recommendation.category)) {
        score += 0.2;
      }
    }

    // Time appropriateness
    if (context.timeOfDay) {
      score += 0.1; // Base bonus for time-aware suggestions
    }

    return Math.min(1.0, score);
  }

  /**
   * Get fallback events when API fails
   */
  getFallbackEvents() {
    return [
      {
        id: 'fallback-library',
        title: 'Check Library Events',
        category: 'education',
        description: 'Visit your local library for current programs',
        location: 'Public Library',
        cost: 'free',
        source: 'fallback'
      },
      {
        id: 'fallback-park',
        title: 'Visit Local Park',
        category: 'outdoor',
        description: 'Enjoy outdoor time at neighborhood parks',
        location: 'Local Parks',
        cost: 'free',
        source: 'fallback'
      }
    ];
  }

  /**
   * Utility functions
   */
  getCurrentSeason(month) {
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'fall';
    return 'winter';
  }

  getLocationContext() {
    // Get location from weather service or localStorage
    const savedLocation = localStorage.getItem('weather-location');
    return savedLocation ? savedLocation.split(',')[0] : 'Local Area';
  }

  /**
   * Filter events by family relevance
   */
  filterEventsByRelevance(events) {
    return events.filter(event => {
      // Filter by age groups if family has specific age ranges
      if (this.ageGroups.size > 0 && event.ageGroups && !event.ageGroups.includes('all')) {
        const hasMatchingAge = event.ageGroups.some(ageGroup => this.ageGroups.has(ageGroup));
        if (!hasMatchingAge) return false;
      }
      
      return true;
    });
  }
}

// Export singleton instance
export const localEventsService = new LocalEventsService();
export default localEventsService;